import React, { useCallback, useEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import {
    Nav,
    Progress,
    Badge,
    BadgeProps,
    Level,
    Text,
    useBreakpoint,
} from '@landr/maestro';
import { useUserCreditsContext } from 'contexts/UserCreditsContext/UserCreditsContext';
import { usePricingConfig } from 'hooks/usePricingConfig';
import isNumber from 'lodash/isNumber';
import { useSubscriptionsAndAllowedFeaturesContext } from 'contexts/SubscriptionsAndAllowedFeaturesContext/SubscriptionsAndAllowedFeaturesContext';
import { RouterLink } from 'components/CommonUi/RouterLink';
import { useFreemiumGatingCta } from 'components/FreemiumGatingCta/FreemiumGatingCta';
import { CONFIG } from 'utils/config';
import { useFreemiumGatingContext } from 'contexts/FreemiumGatingContext/FreemiumGatingContext';
import { analytics } from 'utils/analytics';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import { css, cva } from '@landr/styled-system/css';
import { NextBillingDisclaimerTooltip } from './NextBillingDisclaimerTooltip';

const badgeStyles = cva({
    base: {
        fontWeight: 'bold',
        verticalAlign: 'middle',
    },
    variants: {
        cursor: {
            pointer: { cursor: 'pointer' },
            default: { cursor: 'default' },
        },
    },
    defaultVariants: {
        cursor: 'pointer',
    },
});

const loadingBadgeStyles = css({
    fontWeight: 'bold',
    cursor: 'pointer',
    verticalAlign: 'middle',
    paddingX: 'xs',
});

const LoadingBadge = () => (
    <Badge
        aria-label={t`Loading`}
        size="md"
        kind="subtle"
        variant="primary"
        isRounded
        icon={Progress}
        className={loadingBadgeStyles}
    />
);

const useBalanceBadge = ({
    isPlanPaused,
    isPoorStanding,
    userCredits,
    authenticatedPricingPath,
}: {
    isPlanPaused: boolean;
    isPoorStanding: boolean;
    userCredits?: number;
    authenticatedPricingPath: string;
}) => {
    let to: string | undefined = authenticatedPricingPath;
    let badgeVariant: BadgeProps['variant'] = 'primary';

    if (isPoorStanding) {
        to = CONFIG.VITE_ACCOUNT_BILLING_URL;
        badgeVariant = 'danger';
    } else if (isPlanPaused) {
        to = CONFIG.VITE_ACCOUNT_PLANS_URL;
        badgeVariant = 'warning';
    } else if (userCredits === 0) {
        badgeVariant = 'warning';
    }

    const content = isNumber(userCredits)
        ? t({
              comment: '"cr." is the abbreviation of "credit"',
              message: `${userCredits?.toString(10)} cr.`,
          })
        : undefined;

    return { badgeVariant, content, to };
};

export interface RemainingCreditsContentProps {
    isMobile: boolean;
    content?: string;
    badgeVariant?: BadgeProps;
    withCursorPointer?: boolean;
}

const RemainingCreditsContent: React.FC<RemainingCreditsContentProps> = ({
    isMobile,
    content,
    badgeVariant,
    withCursorPointer = true,
}) => {
    return (
        <Level>
            {isMobile && (
                <Level.Item>
                    <Text textStyle="body-sm">
                        <Trans>Credit balance available</Trans>
                    </Text>
                </Level.Item>
            )}
            <Level.Item>
                {content ? (
                    <Badge
                        aria-label="User Credit Count"
                        size="md"
                        kind="subtle"
                        variant={badgeVariant as BadgeProps['variant']}
                        isRounded
                        showZero={true}
                        max={9999}
                        label={content}
                        className={badgeStyles({
                            cursor: withCursorPointer ? 'pointer' : 'default',
                        })}
                    />
                ) : (
                    <LoadingBadge />
                )}
            </Level.Item>
        </Level>
    );
};

export interface RemainingCreditsProps {
    onClick?: () => void;
    target: '_blank' | '_self';
}

const RemainingCredits: React.FC<RemainingCreditsProps> = ({
    onClick,
    target,
}) => {
    const { isAppGated } = useFreemiumGatingContext();
    const { handleOnClick: handleOnClickFreemiumGating } = useFreemiumGatingCta(
        {
            analyticVariant: 'RemainingCredits',
        },
    );

    const isMobile = useBreakpoint('md');
    const { authenticatedPricingPath } = usePricingConfig();
    const { userCredits } = useUserCreditsContext();

    const { isPlanPaused, isPoorStanding } =
        useSubscriptionsAndAllowedFeaturesContext();

    const [userCreditsLeft, setUserCreditsLeft] = useState(0);

    useEffect(() => {
        setUserCreditsLeft(userCredits as number);
    }, [userCredits]);

    const { badgeVariant, content, to } = useBalanceBadge({
        isPlanPaused,
        isPoorStanding,
        userCredits: userCreditsLeft,
        authenticatedPricingPath,
    });

    const handleOnClick = useCallback(() => {
        onClick?.();

        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.ViewPricing,
            ctaType: CtaType.Button,
            interactionSource: InteractionSource.Navbar,
            ctaText: 'Credits balance',
        });
    }, [handleOnClickFreemiumGating, onClick]);

    const handleVariantOnClick = useCallback(() => {
        handleOnClickFreemiumGating();
        onClick?.();
    }, [handleOnClickFreemiumGating, onClick]);

    if (isAppGated) {
        return (
            <Nav.Link onClick={handleVariantOnClick}>
                <RemainingCreditsContent
                    isMobile={isMobile}
                    content={content}
                    badgeVariant={badgeVariant as BadgeProps}
                />
            </Nav.Link>
        );
    }

    return (
        <Nav.Link
            as={RouterLink}
            to={to}
            target={target}
            onClick={handleOnClick}
        >
            <NextBillingDisclaimerTooltip creditsLeft={userCreditsLeft}>
                <RemainingCreditsContent
                    isMobile={isMobile}
                    content={content}
                    badgeVariant={badgeVariant as BadgeProps}
                />
            </NextBillingDisclaimerTooltip>
        </Nav.Link>
    );
};

export default RemainingCredits;
